import { BellIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Notification } from '../lib/database.types'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../stores/authStore'

interface NotificationWithDetails extends Notification {
  titles: { canonical_name: string }
  chapters: { chapter_no: number; title: string | null }
}

export function NotificationBell(): React.ReactElement | null {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationWithDetails[]>(
    [],
  )
  const [isOpen, setIsOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const fetchNotifications = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('notifications')
      .select('*, titles(canonical_name), chapters(chapter_no, title)')
      .eq('read', false)
      .order('created_at', { ascending: false })
      .limit(20)
    setNotifications((data as NotificationWithDetails[]) ?? [])
  }, [user])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // Close panel on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  async function markRead(n: NotificationWithDetails) {
    await supabase.from('notifications').update({ read: true }).eq('id', n.id)
    setNotifications((prev) => prev.filter((x) => x.id !== n.id))
    setIsOpen(false)
    navigate(`/read/${n.chapter_id}`)
  }

  async function markAllRead() {
    const ids = notifications.map((n) => n.id)
    if (!ids.length) return
    await supabase.from('notifications').update({ read: true }).in('id', ids)
    setNotifications([])
  }

  if (!user) return null

  const unreadCount = notifications.length

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="relative rounded-xl p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 font-bold text-white text-xs leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-gray-100 border-b px-4 py-2">
            <span className="font-semibold text-gray-800 text-sm">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-blue-600 text-xs hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="py-8 text-center text-gray-400 text-sm">
              No new notifications
            </p>
          ) : (
            <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => markRead(n)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <p className="font-medium text-gray-900 text-sm">
                      {n.titles.canonical_name}
                    </p>
                    <p className="text-gray-500 text-xs">
                      New: Chapter {n.chapters.chapter_no}
                      {n.chapters.title ? ` — ${n.chapters.title}` : ''}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
